import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  setCurrentLocale,
  translate,
  type Locale,
  type MessageKey,
  type MessageVars,
} from './messages'
import { I18nContext, type I18nValue } from './context'

const STORAGE_KEY = 'ucsc.lang'

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'hi' || saved === 'en') return saved
  } catch {
    /* storage unavailable */
  }
  return 'hi' // Hindi-first: the phone's users read Hindi.
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  // Screens that read words outside context (the V1 gate screens) follow the same language.
  setCurrentLocale(locale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      toggleLocale: () => setLocale(locale === 'hi' ? 'en' : 'hi'),
      t: (key: MessageKey, vars?: MessageVars) => translate(locale, key, vars),
      pair: (key: MessageKey, vars?: MessageVars) => ({
        hi: translate('hi', key, vars),
        en: translate('en', key, vars),
      }),
    }),
    [locale, setLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
